import { gql, type TypedDocumentNode } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import { Badge, Button } from '@repo/shared-client/components/ui'
import { createFileRoute, Link, Outlet, useLocation } from '@tanstack/react-router'
import { useAuth } from '@web/src/hooks/use-auth'
import type { FriendsCountsQuery, FriendsCountsQueryVariables } from 'types/graphql'

export const FRIENDS_COUNTS_QUERY: TypedDocumentNode<FriendsCountsQuery, FriendsCountsQueryVariables> = gql`
  query FriendsCountsQuery($profileId: String!) {
    friendsCounts(filter: { profileId: $profileId }) {
      friends
      pending
      requests
    }
  }
`

export const Route = createFileRoute('/(auth)/(main-layout)/profile/$profileId/friends')({
  component: Layout,
})

function Layout() {
  const { currentUser, hasPermission } = useAuth()
  const { profileId } = Route.useParams()
  const location = useLocation()
  const pathname = location.pathname

  // The parent route queries the friends counts and makes it available to this route
  const { data } = useQuery(FRIENDS_COUNTS_QUERY, { variables: { profileId }, fetchPolicy: 'cache-only' })

  const isRequestsActive = pathname.includes('/requests')
  const isPendingActive = pathname.includes('/pending')
  const isFriendsActive = !(isRequestsActive || isPendingActive)
  const canViewAll = profileId === currentUser?.profile?.id || hasPermission('profile:read:all')

  return (
    <>
      <div className="flex w-full flex-wrap items-center justify-center gap-2 py-2">
        <Button
          nativeButton={false}
          render={<Link className="no-underline" params={{ profileId } as never} to="/profile/$profileId/friends" />}
          size="sm"
          variant={isFriendsActive ? 'pill' : 'pill-outline'}
        >
          Friends
          <Badge className="min-h-5 min-w-5 px-1" size="sm" variant="secondary">
            {data?.friendsCounts?.friends}
          </Badge>
        </Button>
        {canViewAll && (
          <>
            <Button
              nativeButton={false}
              render={
                <Link
                  className="no-underline"
                  params={{ profileId } as never}
                  to="/profile/$profileId/friends/requests"
                />
              }
              size="sm"
              variant={isRequestsActive ? 'pill' : 'pill-outline'}
            >
              Requests
              {(data?.friendsCounts?.requests ?? 0) > 0 && (
                <Badge className="min-h-5 min-w-5 px-1" size="sm" variant={isRequestsActive ? 'secondary' : 'default'}>
                  {data?.friendsCounts?.requests}
                </Badge>
              )}
            </Button>
            <Button
              nativeButton={false}
              render={
                <Link
                  className="no-underline"
                  params={{ profileId } as never}
                  to="/profile/$profileId/friends/pending"
                />
              }
              size="sm"
              variant={isPendingActive ? 'pill' : 'pill-outline'}
            >
              Pending
              {(data?.friendsCounts?.pending ?? 0) > 0 && (
                <Badge className="min-h-5 min-w-5 px-1" size="sm" variant={isPendingActive ? 'secondary' : 'default'}>
                  {data?.friendsCounts?.pending}
                </Badge>
              )}
            </Button>
          </>
        )}
      </div>
      <Outlet />
    </>
  )
}
